import { useState } from 'react';
import { X, Check, Search } from 'lucide-react';
import type { SongSummary } from '../../types';

interface SongSelectionModalProps {
  songs: SongSummary[];
  selectedSongIds: number[];
  onSave: (songIds: number[]) => void;
  onClose: () => void;
}

export function SongSelectionModal({
  songs,
  selectedSongIds,
  onSave,
  onClose,
}: SongSelectionModalProps) {
  const [selected, setSelected] = useState<Set<number>>(new Set(selectedSongIds));
  const [searchQuery, setSearchQuery] = useState('');

  const handleToggle = (songId: number) => {
    const newSelected = new Set(selected);
    if (newSelected.has(songId)) {
      newSelected.delete(songId);
    } else {
      newSelected.add(songId);
    }
    setSelected(newSelected);
  };

  const handleSelectAll = () => {
    const filteredSongs = getFilteredSongs();
    const newSelected = new Set(selected);
    filteredSongs.forEach((song) => newSelected.add(song.id));
    setSelected(newSelected);
  };

  const handleDeselectAll = () => {
    const filteredSongs = getFilteredSongs();
    const newSelected = new Set(selected);
    filteredSongs.forEach((song) => newSelected.delete(song.id));
    setSelected(newSelected);
  };

  const handleSave = () => {
    onSave(Array.from(selected));
  };

  const getFilteredSongs = (): SongSummary[] => {
    if (!searchQuery.trim()) return songs;

    const query = searchQuery.toLowerCase();
    return songs.filter(
      (song) =>
        song.title.toLowerCase().includes(query) ||
        song.artist.toLowerCase().includes(query)
    );
  };

  const filteredSongs = getFilteredSongs();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-monster-card border border-monster-border rounded-lg z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-monster-border">
          <h2 className="text-xl font-bold">Select Songs</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-monster-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search songs..."
              className="w-full pl-10 pr-4 py-2 bg-monster-dark border border-monster-border rounded text-white placeholder-gray-400 focus:outline-none focus:border-monster-blue"
            />
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="p-4 border-b border-monster-border flex items-center justify-between bg-monster-dark/50">
          <span className="text-sm text-gray-400">
            {selected.size} of {songs.length} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              className="text-sm px-3 py-1.5 bg-monster-blue/10 hover:bg-monster-blue/20 text-monster-blue rounded transition-colors"
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              className="text-sm px-3 py-1.5 bg-monster-crimson/10 hover:bg-monster-crimson/20 text-monster-crimson rounded transition-colors"
            >
              Deselect All
            </button>
          </div>
        </div>

        {/* Song List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {filteredSongs.length > 0 ? (
              filteredSongs.map((song) => (
                <label
                  key={song.id}
                  className="flex items-center gap-3 p-3 bg-monster-dark rounded hover:bg-monster-card cursor-pointer transition-colors group"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(song.id)}
                    onChange={() => handleToggle(song.id)}
                    className="w-5 h-5 rounded border-2 border-gray-400 checked:bg-monster-blue checked:border-monster-blue transition-colors cursor-pointer"
                  />
                  <img
                    src={song.imagePath}
                    alt={song.title}
                    className="w-12 h-12 rounded object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{song.title}</p>
                    <p className="text-xs text-gray-400 truncate">{song.artist}</p>
                  </div>
                  {selected.has(song.id) && (
                    <Check className="text-monster-blue flex-shrink-0" size={20} />
                  )}
                </label>
              ))
            ) : (
              <p className="text-center text-gray-400 py-12">
                {searchQuery ? 'No songs match your search' : 'No songs available'}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-monster-border flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary">
            Save Selection ({selected.size})
          </button>
        </div>
      </div>
    </>
  );
}
